/** Builds the ChatGPT hand-off for URLs Google did not index.
 * Single source of truth for the SEO prompt — do not duplicate the copy in components.
 */

import type { InspectTimelineEntry } from '../api/types';
import { toUtcIso } from './datetime';
import { verdictLabel, type InspectDetail } from './inspect-detail';

/** Cloudflare in front of chatgpt.com rejects request lines beyond 16KB.
 * Persian text costs ~6 chars once percent-encoded, so the prompt sends the
 * newest checks in full and squeezes the rest into one line each.
 */
const MAX_CHAT_URL_LENGTH = 15_000;
const MAX_DETAILED_SNAPSHOTS = 6;
const MAX_LIST_ITEMS = 3;

export interface SeoAdviceSnapshot {
  createdAt?: string | null;
  verdict?: string | null;
  coverageState?: string | null;
  robotsTxtState?: string | null;
  pageFetchState?: string | null;
  indexingState?: string | null;
  crawledAs?: string | null;
  lastCrawlTime?: string | null;
  sitemapLastmod?: string | null;
  googleCanonical?: string | null;
  userCanonical?: string | null;
  referringUrls?: string[];
  sitemaps?: string[];
  sourceLabel?: string | null;
  indexRequest?: {
    requestedAt: string;
    success: boolean;
    daysSinceRequest: number;
  } | null;
  errorMessage?: string | null;
  httpStatus?: number | null;
}

export function timelineEntryToSnapshot(
  entry: InspectTimelineEntry,
): SeoAdviceSnapshot {
  const source = entry.inspectSource;
  const sourceLabel = source
    ? source.trigger === 'schedule'
      ? source.scheduleName
        ? `زمان‌بندی (${source.scheduleName})`
        : 'زمان‌بندی'
      : 'دستی'
    : null;

  return {
    createdAt: entry.createdAt,
    verdict: entry.verdict,
    coverageState: entry.coverageState,
    robotsTxtState: entry.robotsTxtState,
    pageFetchState: entry.pageFetchState,
    indexingState: entry.indexingState,
    crawledAs: entry.crawledAs,
    lastCrawlTime: entry.lastCrawlTime,
    sitemapLastmod: entry.sitemapLastmod,
    googleCanonical: entry.googleCanonical,
    userCanonical: entry.userCanonical,
    referringUrls: entry.referringUrls,
    sitemaps: entry.sitemaps,
    sourceLabel,
    indexRequest: entry.lastIndexRequest
      ? {
          requestedAt: entry.lastIndexRequest.requestedAt,
          success: entry.lastIndexRequest.success,
          daysSinceRequest: entry.lastIndexRequest.daysSinceRequest,
        }
      : null,
    errorMessage: entry.error?.message ?? null,
    httpStatus: entry.error?.httpStatus ?? null,
  };
}

export function inspectDetailToSnapshot(
  detail: InspectDetail,
): SeoAdviceSnapshot {
  return {
    createdAt: detail.createdAt,
    verdict: detail.verdict,
    coverageState: detail.coverageState,
    robotsTxtState: detail.robotsTxtState,
    pageFetchState: detail.pageFetchState,
    indexingState: detail.indexingState,
    crawledAs: detail.crawledAs,
    lastCrawlTime: detail.lastCrawlTime,
    sitemapLastmod: detail.sitemapLastmod,
    googleCanonical: detail.googleCanonical,
    userCanonical: detail.userCanonical,
    referringUrls: detail.referringUrls,
    sitemaps: detail.sitemaps,
    errorMessage: detail.errorDetail ?? null,
    httpStatus: detail.httpStatus ?? null,
  };
}

/** Minute-precision UTC — seconds and milliseconds only cost URL budget. */
function instant(value?: string | null): string | null {
  const iso = value ? toUtcIso(value) : null;
  return iso ? `${iso.slice(0, 16)}Z` : null;
}

function listValue(values?: string[]): string | null {
  if (!values || values.length === 0) return null;
  const head = values.slice(0, MAX_LIST_ITEMS).join(' , ');
  const rest = values.length - MAX_LIST_ITEMS;
  return rest > 0 ? `${head} (و ${rest} مورد دیگر)` : head;
}

function snapshotBlock(snapshot: SeoAdviceSnapshot, index: number): string {
  const lines: string[] = [];
  const at = instant(snapshot.createdAt);
  const title = index === 0 ? 'آخرین بررسی' : `بررسی #${index + 1}`;
  lines.push(`[${title}] زمان بررسی: ${at ?? 'نامشخص'}`);

  if (snapshot.verdict) {
    lines.push(`verdict: ${snapshot.verdict} (${verdictLabel(snapshot.verdict)})`);
  }
  if (snapshot.coverageState) {
    lines.push(`coverageState: ${snapshot.coverageState}`);
  }

  const states = [
    snapshot.robotsTxtState ? `robotsTxtState: ${snapshot.robotsTxtState}` : null,
    snapshot.pageFetchState ? `pageFetchState: ${snapshot.pageFetchState}` : null,
    snapshot.indexingState ? `indexingState: ${snapshot.indexingState}` : null,
    snapshot.crawledAs ? `crawledAs: ${snapshot.crawledAs}` : null,
  ].filter(Boolean);
  if (states.length > 0) lines.push(states.join(' | '));

  const lastCrawl = instant(snapshot.lastCrawlTime);
  if (lastCrawl) lines.push(`lastCrawlTime: ${lastCrawl}`);
  const lastmod = instant(snapshot.sitemapLastmod);
  if (lastmod) lines.push(`sitemapLastmod: ${lastmod}`);
  if (snapshot.userCanonical) {
    lines.push(`userCanonical: ${snapshot.userCanonical}`);
  }
  if (snapshot.googleCanonical) {
    lines.push(`googleCanonical: ${snapshot.googleCanonical}`);
  }

  const sitemaps = listValue(snapshot.sitemaps);
  if (sitemaps) lines.push(`sitemaps: ${sitemaps}`);
  const referring = listValue(snapshot.referringUrls);
  if (referring) lines.push(`referringUrls: ${referring}`);

  if (snapshot.sourceLabel) lines.push(`منبع این بررسی: ${snapshot.sourceLabel}`);

  if (snapshot.indexRequest) {
    const requestedAt = instant(snapshot.indexRequest.requestedAt);
    const outcome = snapshot.indexRequest.success ? 'موفق' : 'ناموفق';
    lines.push(
      `درخواست ایندکس ثبت‌شده توسط ما: ${requestedAt ?? 'نامشخص'} — ${outcome} — ${snapshot.indexRequest.daysSinceRequest} روز پیش`,
    );
  }

  if (snapshot.errorMessage || snapshot.httpStatus != null) {
    const parts = [
      snapshot.errorMessage,
      snapshot.httpStatus != null ? `HTTP ${snapshot.httpStatus}` : null,
    ].filter(Boolean);
    lines.push(`خطا: ${parts.join(' — ')}`);
  }

  return lines.join('\n');
}

function snapshotLine(snapshot: SeoAdviceSnapshot): string {
  return [
    instant(snapshot.createdAt) ?? '?',
    snapshot.verdict ?? '?',
    snapshot.coverageState ?? '?',
    instant(snapshot.lastCrawlTime) ?? '?',
    snapshot.pageFetchState ?? '?',
  ].join(' | ');
}

function composePrompt(
  url: string,
  snapshots: SeoAdviceSnapshot[],
  detailedCount: number,
  omittedCount = 0,
): string {
  const detailed = snapshots
    .slice(0, detailedCount)
    .map((snapshot, index) => snapshotBlock(snapshot, index))
    .join('\n\n');
  const older = snapshots.slice(detailedCount);
  const omitted =
    omittedCount > 0
      ? `\n(${omittedCount} بررسی قدیمی‌تر به دلیل محدودیت طول پیام ارسال نشده است.)`
      : '';
  const history =
    older.length > 0
      ? `${detailed}\n\nخلاصه بررسی‌های قدیمی‌تر (زمان بررسی | verdict | coverageState | آخرین خزش | واکشی):\n${older
          .map(snapshotLine)
          .join('\n')}${omitted}`
      : `${detailed}${omitted}`;

  return `نقش تو: متخصص ارشد سئوی فنی (Technical SEO) با تسلط کامل بر Google Search Console، فرآیند خزش و ایندکس گوگل، canonical، robots، رندر جاوااسکریپت و کیفیت محتوا.

هدف: صفحه زیر در گوگل وضعیت سالمی ندارد (ایندکس نشده یا از ایندکس خارج شده). علت ریشه‌ای را پیدا کن و راه‌حل عملی و دقیق بده.

آدرس صفحه: ${url}

۱) بررسی زنده صفحه — اجباری و پیش از هر تحلیلی
همین آدرس را واقعاً باز کن و محتوایش را بخوان؛ فقط به داده‌های بخش ۲ اکتفا نکن. این موارد را مستقیم از خود صفحه استخراج کن: کد وضعیت HTTP و زنجیره ریدایرکت، تگ title و meta description و ساختار H1/H2، تگ canonical و آدرسی که به آن اشاره می‌کند، meta robots و هدر X-Robots-Tag، حجم و یکتا بودن محتوای متنی، وابستگی محتوا به جاوااسکریپت، داده ساختاریافته (JSON-LD)، فایل robots.txt همان دامنه برای این مسیر، حضور آدرس در sitemap، و نتیجه جست‌وجوی site: برای همین آدرس در گوگل. اگر به هر دلیلی نتوانستی صفحه را باز کنی، این را صریح بنویس و سپس فقط با داده‌های بخش ۲ ادامه بده.

۲) داده‌های واقعی Google Search Console (خروجی URL Inspection API، از جدید به قدیم)
${history}

۳) تحلیل
یافته‌های صفحه زنده را با داده‌های بالا تطبیق بده و هر تناقضی را صریح بگو. روند تاریخچه را بررسی کن: وضعیت از چه زمانی و پس از چه اتفاقی تغییر کرده است. اگر درخواست ایندکس ثبت شده ولی صفحه هنوز ایندکس نشده، دلیل واقعی بی‌اثر بودن آن را توضیح بده. علت ریشه‌ای را از علامت جدا کن.

۴) ساختار پاسخ — فارسی و دقیقاً با همین ترتیب
- تشخیص نهایی: علت ریشه‌ای در یک پاراگراف کوتاه.
- جدول «علت محتمل | شواهد | درجه اطمینان».
- اقدام فوری (۲۴ تا ۴۸ ساعت آینده): گام‌به‌گام، با مسیر یا فایل دقیق و نمونه کد آماده کپی (HTML، هدر HTTP، robots.txt، تنظیم nginx یا htaccess، یا تنظیم افزونه سئو).
- اقدام میان‌مدت (۲ تا ۶ هفته): محتوا، لینک‌سازی داخلی، ساختار سایت و سرعت.
- چک‌لیست تأیید: بعد از اعمال تغییرات، چه چیزی را کجا و چند روز بعد بررسی کنم تا مطمئن شوم حل شده است.
- ریسک‌ها و کارهایی که نباید انجام دهم.

قواعد: عملیاتی بنویس و از توصیه کلی مثل «کیفیت محتوا را بالا ببرید» بدون جزئیات پرهیز کن؛ هر ادعا را به شاهدی مشخص از بخش ۱ یا ۲ وصل کن؛ حدس را از واقعیت جدا کن؛ اگر برای نتیجه‌گیری داده کم داری، بگو دقیقاً چه داده‌ای لازم است و چطور آن را تهیه کنم.`;
}

function toChatUrl(prompt: string): string {
  return `https://chatgpt.com/?hints=search&q=${encodeURIComponent(prompt)}`;
}

/** ChatGPT deep link carrying the page URL plus its Search Console history. */
export function buildSeoAdviceChatUrl(
  url: string,
  snapshots: SeoAdviceSnapshot[],
): string {
  const history = snapshots.length > 0 ? snapshots : [{}];

  const start = Math.min(history.length, MAX_DETAILED_SNAPSHOTS);
  for (let detailed = start; detailed > 1; detailed -= 1) {
    const candidate = toChatUrl(composePrompt(url, history, detailed));
    if (candidate.length <= MAX_CHAT_URL_LENGTH) return candidate;
  }

  // Still over budget: keep the newest check detailed and drop the oldest lines.
  for (let count = history.length; count > 1; count -= 1) {
    const candidate = toChatUrl(
      composePrompt(url, history.slice(0, count), 1, history.length - count),
    );
    if (candidate.length <= MAX_CHAT_URL_LENGTH) return candidate;
  }
  return toChatUrl(
    composePrompt(url, history.slice(0, 1), 1, history.length - 1),
  );
}
